(() => {
  if (customElements.get('bowl-design-video')) return;
  let videoNumber = 0;

  class BowlDesignVideo extends HTMLElement {
    manualPaused = false;
    manualPlay = false;
    autoplayBlocked = false;
    visible = false;
    playPending = false;
    playAttempt = 0;

    connectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      this.video = this.querySelector('video');
      this.button = this.querySelector('button');
      if (!this.video || !this.button) return;

      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.visible = false;
      this.video.controls = false;
      this.video.muted = true;
      this.video.loop = true;
      this.video.playsInline = true;
      this.video.id ||= `bowl-suction-video-${++videoNumber}`;
      this.button.setAttribute('aria-controls', this.video.id);
      this.button.hidden = false;

      this.button.addEventListener('click', () => this.toggle(), options);
      this.video.addEventListener('play', () => {
        if (!this.shouldPlay()) this.pause();
        this.render();
      }, options);
      this.video.addEventListener('pause', () => this.render(), options);
      this.video.addEventListener('playing', () => this.render(), options);
      this.video.addEventListener('error', () => {
        this.autoplayBlocked = true;
        this.pause();
      }, options);
      document.addEventListener('visibilitychange', () => this.sync(), options);
      window.addEventListener('pagehide', () => this.pause(), options);
      window.addEventListener('pageshow', () => this.sync(), options);
      this.motion.addEventListener('change', () => {
        this.manualPlay = false;
        this.sync();
      }, options);

      this.observer = new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting && entry.intersectionRatio >= 0.25;
        this.sync();
      }, { threshold: [0, 0.25] });
      this.observer.observe(this);
      this.render();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      this.visible = false;
      this.pause();
    }

    shouldPlay() {
      return this.isConnected && this.visible && !document.hidden &&
        !this.manualPaused && !this.autoplayBlocked &&
        (!this.motion.matches || this.manualPlay);
    }

    toggle() {
      if (!this.video.paused || this.playPending) {
        this.manualPaused = true;
        this.manualPlay = false;
        this.pause();
      } else {
        this.manualPaused = false;
        this.manualPlay = true;
        this.autoplayBlocked = false;
        this.sync();
      }
    }

    sync() {
      if (!this.shouldPlay()) {
        this.pause();
        return;
      }
      if (!this.video.paused || this.playPending) return;

      // No video request is made until an eligible play attempt occurs in view.
      if (!this.video.hasAttribute('src')) this.video.src = this.video.dataset.src;
      const attempt = ++this.playAttempt;
      this.playPending = true;
      this.render();

      let playback;
      try {
        playback = this.video.play();
      } catch {
        this.rejectPlay(attempt);
        return;
      }
      Promise.resolve(playback).then(() => {
        if (attempt !== this.playAttempt) return;
        this.playPending = false;
        if (!this.shouldPlay()) this.pause();
        this.render();
      }).catch(() => this.rejectPlay(attempt));
    }

    rejectPlay(attempt) {
      if (attempt !== this.playAttempt) return;
      // A browser autoplay refusal leaves an explicit Play button for retry.
      this.autoplayBlocked = true;
      this.manualPlay = false;
      this.pause();
    }

    pause() {
      ++this.playAttempt;
      this.playPending = false;
      this.video?.pause();
      this.render();
    }

    render() {
      if (!this.video || !this.button) return;
      const action = !this.video.paused || this.playPending ? 'Pause' : 'Play';
      this.button.dataset.action = action.toLowerCase();
      this.button.setAttribute('aria-label', `${action} suction demonstration`);
    }
  }

  customElements.define('bowl-design-video', BowlDesignVideo);
})();
